import { useEffect, useMemo, useState } from 'react';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { calculateTotals, handleUpdateRowFields } from '../../utils/calc-util';

import { AritoFormV2, ConfirmationDialog, HeaderTabsV2 } from '@/components/custom/erp';

import { useFormFieldState } from '../../hooks/useFormFieldState';
import { formSchema, initialFormValues } from '../../schema';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useAuth } from '@/contexts/auth-context';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { FormMode } from '@/types/form';

import type { PhieuXuatDieuChuyen, PhieuXuatDieuChuyenFormData } from '@/types/schemas';
import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import { OtherTab } from './OtherTab';
import BottomBar from './BottomBar';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: PhieuXuatDieuChuyen | null;
  onSubmit?: (data: PhieuXuatDieuChuyenFormData) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();

  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: setDetailNonWritableFields,
    setPasteConfigs: setDetailPasteConfigs
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields);
  const { state, actions } = useFormFieldState(initialData);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const { so_luong, thanh_tien } = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  const handleSubmit = (data: PhieuXuatDieuChuyenFormData) => {
    const formData = transformFormData(data, state, detailRows, entityUnit, so_luong, thanh_tien, formMode);
    onSubmit?.(formData);

    setIsFormDirty(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={(initialData || initialFormValues) as unknown as Partial<PhieuXuatDieuChuyenFormData>}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu xuất điều chuyển'
        onSubmit={handleSubmit}
        onClose={handleClose}
        schema={formSchema}
        headerFields={
          <div onChange={() => setIsFormDirty(true)}>
            <HeaderTabsV2
              tabs={[
                {
                  id: 'info',
                  label: 'Thông tin',
                  component: <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
                },
                ...(formMode === 'view'
                  ? [
                      {
                        id: 'history',
                        label: 'Lịch sử',
                        component: <HistoryTab />
                      }
                    ]
                  : [])
              ]}
              actionButtons={
                formMode !== 'add' && (
                  <InfoDropdown uuid={initialData?.uuid || ''} ma_ct={initialData?.chung_tu || undefined} />
                )
              }
              onTabChange={handleTabChange}
              defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'history' ? 1 : 0}
            />
          </div>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  onRepeatForRows={handleRepeatDetailRows}
                  handleUpdateNonWritableFields={setDetailNonWritableFields}
                  handleUpdatePasteConfigs={setDetailPasteConfigs}
                />
              )
            },

            {
              id: 'other',
              label: 'P/X kho kiêm v/c nội bộ(Chờ phát hành)',
              component: <OtherTab formMode={formMode} formState={{ state, actions }} />
            }
          ]
        }
        bottomBar={activeTab === 'info' && <BottomBar totals={{ so_luong, thanh_tien }} />}
      />
    </>
  );
};

export { FormDialog };
